import { useCallback, useEffect, useState } from 'react';

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'pdf-toolkit-theme';

function readStored(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
  } catch {
    // Private mode or blocked storage — fall back to following the OS.
  }
  return 'system';
}

/**
 * Three-way theme preference, matching the other ON Services apps.
 *
 * "system" removes the attribute entirely and lets the CSS media query decide,
 * rather than resolving the OS preference in JavaScript — so a viewer who
 * changes their OS theme sees the app follow immediately, with no listener.
 */
export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void } {
  const [theme, setThemeState] = useState<Theme>(readStored);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Preference just won't survive a reload; the page still works.
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => setThemeState(next), []);

  return { theme, setTheme };
}
